import type { FC } from "react";
import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useBoolean, useCopyToClipboard } from "usehooks-ts";

import { SectionRole } from "../../../shared/types/roles";
import { CloseOverlayButton } from "../sidebar/CloseOverlayButton";
import { ContentSidebarHeader } from "../sidebar/ContentSidebarHeader";
import { ContentSidebarMain } from "../sidebar/ContentSidebarMain";
import { OverlayContainer } from "../sidebar/OverlayContainer";

import { SectionRoleCard } from "./SectionRoleCard";
import { DisabledUsersCard } from "./SectionUserCard";

import {
  serverCreateSectionInvite,
  serverDeleteSectionInvite,
  serverGetSectionInvite,
} from "api/api-server-actions";
import { LoadingScreen } from "components/LoadingScreen";
import { SecondaryOverlay } from "components/constants";
import type { SectionInviteType } from "components/server-types";
import { getSidebarHeader } from "helpers/sidebar";
import { t } from "i18n/i18n";
import { ConfirmationDialog } from "mds/components/ConfirmationDialog";
import { Dialog } from "mds/components/Dialog";
import { IconText } from "mds/components/IconText";
import { Menu, MenuItem } from "mds/components/Menu";
import { TextField } from "mds/components/TextField";
import { Tooltip } from "mds/components/Tooltip";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import {
  AddPlusIcon,
  CircleInfoIcon,
  DotMenuVerticalIcon,
  DuplicateCopyIcon,
  LongFormPencilIcon,
  PersonClassIcon,
  StarIcon,
  TrashDeleteIcon,
} from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  selectCurrentSectionId,
  selectCurrentSectionIsArchived,
  selectCurrentUserId,
  selectEnhancedUsersWithSectionActivityInCurrentSection,
  selectOrgUserByUserIdMap,
  selectSectionUserByUserIdMap,
} from "store/selectors";
import { useFullListSelector, useFullObjectSelector } from "store/store-hooks";
import { toastCreateOperationCatcher, toastLocalizedOperationCatcher } from "utils/alerts";
import { CONTENT_SIDEBAR_TAB_NAME } from "utils/urls";

export const SectionUsersSidebarPanel = () => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  const isArchived = useAppSelector(selectCurrentSectionIsArchived);
  // Full enhanced users passed through to SectionUserCard / SectionUserItem.
  const users = useFullListSelector(selectEnhancedUsersWithSectionActivityInCurrentSection);
  const { canAuthorSection, canEditEnrollment } = useCapabilities();
  // Record keyed by userId; looked up dynamically per user.
  const orgUserMap = useFullObjectSelector(selectOrgUserByUserIdMap);
  // Record keyed by userId; looked up dynamically per user.
  const sectionUserMap = useFullObjectSelector(selectSectionUserByUserIdMap);

  const userId = useAppSelector(selectCurrentUserId);

  const [sectionInvite, setSectionInvite] = useState<SectionInviteType | null>(null);
  const {
    value: inviteStudentDialogIsShown,
    setTrue: showInviteStudentDialog,
    setFalse: hideInviteStudentDialog,
  } = useBoolean(false);
  const {
    value: deleteInvitationDialogIsShown,
    setTrue: showDeleteInvitationDialog,
    setFalse: hideDeleteInvitationDialog,
  } = useBoolean(false);

  const currentUserInThisSection = useMemo(
    () => users.find((user) => user.id === userId),
    [users, userId],
  );

  const canAddSelfToSection = canEditEnrollment && !currentUserInThisSection;

  const onJoinAsInstructor = useCallback(async () => {
    if (!sectionId || !userId) {
      return;
    }
    try {
      await storeApi.section_users.create(
        {
          section_id: sectionId,
          user_id: userId,
          role: SectionRole.INSTRUCTOR,
        },
        { skipToast: true },
      );
      await storeApi.class_users.create({
        section_id: sectionId,
        user_id: userId,
      });
    } catch (error) {
      toastLocalizedOperationCatcher("join_as_instructor")(error as Error);
    }
  }, [sectionId, userId]);

  const enabledUsers = useMemo(
    () =>
      users.filter((user) => {
        const sectionUser = sectionUserMap[user.id];
        if (sectionUser?.is_disabled) {
          return false;
        }
        const orgUser = orgUserMap?.[user.id];
        if (!orgUser) {
          return true;
        }
        return !orgUser.is_disabled;
      }),
    [users, orgUserMap, sectionUserMap],
  );

  const disabledUsers = useMemo(
    () =>
      users.filter((user) => {
        const sectionUser = sectionUserMap[user.id];
        if (sectionUser?.is_disabled) {
          return true;
        }
        const orgUser = orgUserMap?.[user.id];
        if (!orgUser) {
          return false;
        }
        return orgUser.is_disabled;
      }),
    [users, orgUserMap, sectionUserMap],
  );

  const emailAddresses = useMemo(() => {
    const otherUsers = enabledUsers.filter((user) => user.id !== userId);
    const emails: string[] = [];
    for (const user of otherUsers) {
      if (user.email) emails.push(user.email);
    }
    return emails.join(",");
  }, [enabledUsers, userId]);

  if (!sectionId) {
    return <LoadingScreen />;
  }

  const canSeeSectionAssistants =
    canAuthorSection &&
    users.some((u) => sectionUserMap[u.id]?.role === SectionRole.SECTION_ASSISTANT);

  return (
    <OverlayContainer>
      <ContentSidebarHeader panelType="overlay" visibleOnMobile>
        <IconText
          iconStart={<PersonClassIcon className="text-blue" />}
          text={getSidebarHeader(CONTENT_SIDEBAR_TAB_NAME.Users)}
        />

        <div className="flex items-center justify-end gap-2">
          {(canEditEnrollment || (canAuthorSection && emailAddresses)) && (
            <Menu
              direction="bottom"
              menuButton={
                <SquareIconButton
                  aria-label={t("common.options")}
                  eventName="Section Users - Options"
                  kind="tertiary"
                  size="s"
                  onClick={(e: MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <DotMenuVerticalIcon />
                </SquareIconButton>
              }
              menuClassName="min-w-[180px]"
            >
              {canEditEnrollment && (
                <MenuItem
                  disabled={isArchived}
                  eventName="Section Users - Add students"
                  onClick={showInviteStudentDialog}
                >
                  <IconText
                    iconStart={<AddPlusIcon />}
                    text={t("user_options_menu.invite_students.button")}
                  />
                </MenuItem>
              )}

              {emailAddresses && (
                <MenuItem
                  eventName="Section Users - Email all"
                  href={`mailto:${emailAddresses}`}
                  target="_blank"
                  allowPropagation
                >
                  <IconText
                    iconStart={<LongFormPencilIcon />}
                    text={t("user_options_menu.email_all")}
                  />
                </MenuItem>
              )}
            </Menu>
          )}

          <CloseOverlayButton focusPanelTrigger={SecondaryOverlay.Users} />
        </div>
      </ContentSidebarHeader>

      <ContentSidebarMain bottomPadding={false}>
        {canAddSelfToSection && (
          <Tooltip content={t("user_options_menu.join_as_instructor.tooltip")}>
            <SquareButton className="mb-4" kind="tertiary" size="xs" onClick={onJoinAsInstructor}>
              <span>{t("user_options_menu.join_as_instructor.title")}</span>
              <CircleInfoIcon />
            </SquareButton>
          </Tooltip>
        )}

        <SectionRoleCard
          className="mb-4 mt-2"
          headerIcon={<StarIcon size="xs" />}
          sectionRole={SectionRole.INSTRUCTOR}
          users={enabledUsers}
        />

        {canSeeSectionAssistants && (
          <SectionRoleCard
            className="mb-4 mt-2"
            sectionRole={SectionRole.SECTION_ASSISTANT}
            users={enabledUsers}
          />
        )}

        <SectionRoleCard
          className="mb-4"
          sectionRole={SectionRole.STUDENT}
          users={enabledUsers}
          separateUsersInClassSession
          showEmpty
          showOnlineCount
          startOpen
        />

        {canAuthorSection && <DisabledUsersCard className="mb-4" users={disabledUsers} />}
      </ContentSidebarMain>

      <InviteStudentDialog
        open={inviteStudentDialogIsShown}
        sectionId={sectionId}
        sectionInvite={sectionInvite}
        setSectionInvite={setSectionInvite}
        showDeleteInvitationDialog={showDeleteInvitationDialog}
        onClose={hideInviteStudentDialog}
      />
      <ConfirmationDialog
        confirmButtonKind="caution"
        confirmButtonText={t("user_options_menu.invite_students.action_delete")}
        description={t("user_options_menu.invite_students.warning_delete")}
        open={deleteInvitationDialogIsShown}
        title={t("user_options_menu.invite_students.delete_title")}
        onClose={(confirmed) => {
          hideDeleteInvitationDialog();
          if (!confirmed) {
            showInviteStudentDialog();
          }
        }}
        onConfirm={async () => {
          if (!sectionInvite) {
            return;
          }
          await serverDeleteSectionInvite(sectionInvite.id);
          setSectionInvite(null);
        }}
      />
    </OverlayContainer>
  );
};

interface InviteStudentDialogProps {
  sectionId: string;
  sectionInvite: SectionInviteType | null;
  setSectionInvite: (invite: SectionInviteType | null) => void;
  showDeleteInvitationDialog: () => void;
  open: boolean;
  onClose: () => void;
}

export const InviteStudentDialog: FC<InviteStudentDialogProps> = ({
  sectionId,
  sectionInvite,
  setSectionInvite,
  showDeleteInvitationDialog,
  open,
  onClose,
}) => {
  const dialogTitle = t("user_options_menu.invite_students.title");
  const [recentlyCopied, setRecentlyCopied] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const [, copyToClipboard] = useCopyToClipboard();

  const link = `${window.location.origin}/?invite_key=${sectionInvite?.invite_key}`;

  // TODO: Replace with storeApi
  useEffect(() => {
    serverGetSectionInvite(sectionId)
      // We assume there's only one invite per course right now
      .then((invites) => setSectionInvite(invites[0]))
      .catch(setError)
      .finally(() => setLoading(false));
  }, [sectionId, setSectionInvite]);

  const onCreateInvite = async () => {
    const invite = await serverCreateSectionInvite(sectionId).catch(
      toastCreateOperationCatcher("invite"),
    );

    setSectionInvite(invite || null);
  };

  const onCopy = async () => {
    await copyToClipboard(link);
    setRecentlyCopied(true);
    setTimeout(() => setRecentlyCopied(false), 2000);
  };

  if (error) {
    return (
      <Dialog open={open} title={dialogTitle} onClose={onClose}>
        <div className="px-6 pb-6">
          <h3 className="my-2">{t("user_options_menu.invite_students.error_title")}</h3>
          <div className="body-s">
            {t("user_options_menu.invite_students.error_body", { error_message: error?.message })}
          </div>
        </div>
      </Dialog>
    );
  }

  if (isLoading) {
    return (
      <Dialog open={open} title={dialogTitle} onClose={onClose}>
        <div className="px-6 pb-6">
          <LoadingScreen />
        </div>
      </Dialog>
    );
  }

  if (!sectionInvite) {
    return (
      <Dialog open={open} title={dialogTitle} onClose={onClose}>
        <div className="flex flex-col items-center gap-5">
          <div className="body-s">{t("user_options_menu.invite_students.explanation")}</div>
          <div className="flex w-full justify-end gap-2">
            <SquareButton kind="tertiary" size="m" onClick={onClose}>
              {t("common.cancel")}
            </SquareButton>
            <SquareButton
              eventName="New invitation modal - Confirm"
              kind="accent"
              size="m"
              onClick={() => onCreateInvite()}
            >
              {t("user_options_menu.invite_students.action_create")}
            </SquareButton>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} title={dialogTitle} onClose={onClose}>
      <div className="flex flex-col items-start">
        <div className="body-s mb-6">{t("user_options_menu.invite_students.label")}</div>

        <div className="my-3 mb-6 flex w-full justify-center">
          <img
            alt={t("user_options_menu.invite_students.qr_code_alt_text")}
            className="size-32"
            src={`https://api.qrserver.com/v1/create-qr-code/?size=128x128&data=${link}`}
          />
        </div>

        <div className="mt-3 flex items-center gap-2">
          <SquareIconButton
            aria-label={t("user_options_menu.invite_students.action_delete")}
            eventName="Delete student invitation link"
            kind="caution"
            size="l"
            onClick={() => {
              showDeleteInvitationDialog();
              onClose();
            }}
          >
            <TrashDeleteIcon />
          </SquareIconButton>

          <TextField value={link} readOnly />

          <div className="flex items-center">
            <Tooltip
              content={
                recentlyCopied
                  ? t("user_options_menu.invite_students.copy_link_success")
                  : t("user_options_menu.invite_students.copy_link")
              }
            >
              <SquareButton
                eventName="Copy student invitation link"
                kind="accent"
                size="m"
                title={t("user_options_menu.invite_students.copy_link")}
                onClick={onCopy}
              >
                <IconText
                  iconStart={<DuplicateCopyIcon />}
                  text={t("user_options_menu.invite_students.copy_link")}
                />
              </SquareButton>
            </Tooltip>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
